import { useState, useEffect, useRef } from "react";
import { Moon, Sun, ImageIcon, ChevronLeft, Check, Lock } from "lucide-react";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useBackground } from "../../hooks/useBackground";

export default function ThemeDropdown({
  longestStreak = 0,
  isPremium = false,
}) {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { bgIndex, selectBg, backgrounds, desktopBackgrounds } = useBackground(isPremium);

  const [open, setOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const wrapperRef = useRef(null);

  const [isDesktopPreview, setIsDesktopPreview] = useState(() => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 1024px)").matches;
});

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowPicker(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) setShowPicker(false);
  }, [open]);

  useEffect(() => {
  const media = window.matchMedia("(min-width: 1024px)");

  const updatePreviewMode = (e) => {
    setIsDesktopPreview(e.matches);
  };

  setIsDesktopPreview(media.matches);

  if (typeof media.addEventListener === "function") {
    media.addEventListener("change", updatePreviewMode);
    return () => media.removeEventListener("change", updatePreviewMode);
  }

  media.addListener(updatePreviewMode);
  return () => media.removeListener(updatePreviewMode);
}, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="bg-toggle"
        onClick={() => setOpen((p) => !p)}
        aria-label="Personnaliser l'apparence"
        aria-expanded={open}
      >
        🎨
      </button>

      {/* ── Menu principal ─────────────────────── */}
      {open && !showPicker && (
        <div className="theme-dropdown">
          <button
            type="button"
            className="theme-dropdown__item"
            onClick={() => {
              toggleDarkMode();
              setOpen(false);
            }}
          >
            <span className="theme-dropdown__icon">
              {isDarkMode ? <Sun size={15} /> : <Moon size={15} />}
            </span>
            <span className="theme-dropdown__label">
              {isDarkMode ? "Mode clair" : "Mode sombre"}
            </span>
            <span className="theme-dropdown__toggle">
              <span className="theme-dropdown__pill" data-active={isDarkMode} />
            </span>
          </button>

          <div className="theme-dropdown__divider" />

          <button
            type="button"
            className="theme-dropdown__item"
            onClick={() => setShowPicker(true)}
          >
            <span className="theme-dropdown__icon">
              <ImageIcon size={15} />
            </span>
            <span className="theme-dropdown__label">Changer le fond</span>
            <span className="theme-dropdown__arrow">›</span>
          </button>
        </div>
      )}

      {/* ── Sous-menu : sélecteur de fond ─────── */}
      {open && showPicker && (
        <div className="theme-dropdown theme-dropdown--picker">
          <button
            type="button"
            className="theme-dropdown__back"
            onClick={() => setShowPicker(false)}
          >
            <ChevronLeft size={14} />
            <span>Fond d'écran</span>
          </button>

          <div className="theme-dropdown__divider" />

          <div className="bg-picker-grid">
{backgrounds.map((bg, i) => {
  const isLocked = (bg.streakRequired ?? 0) > longestStreak;
  const isActive = i === bgIndex;

  const thumbSrc =
    isDesktopPreview && desktopBackgrounds?.[i]?.file
      ? desktopBackgrounds[i].file
      : bg.file;

              return (
                <button
                  type="button"
                  key={bg.file}
                  className={[
                    "bg-picker-item",
                    isActive ? "bg-picker-item--active" : "",
                    isLocked ? "bg-picker-item--locked" : "",
                  ].join(" ").trim()}
                  onClick={() => {
                    if (isLocked) return;
                    selectBg(i);
                    setOpen(false);
                    setShowPicker(false);
                  }}
                  disabled={isLocked}
                  title={
                    isLocked
                      ? `Débloqué à ${bg.streakRequired} jours consécutifs`
                      : bg.label
                  }
                  aria-disabled={isLocked}
                >
<div
  className="bg-picker-thumb"
  style={{ backgroundImage: `url(${thumbSrc})` }}
>
                    {/* Overlay verrouillé */}
                    {isLocked && (
                      <div className="bg-picker-lock-overlay">
                        <Lock size={12} strokeWidth={2.5} />
                        <span>{bg.streakRequired}j</span>
                      </div>
                    )}

                    {/* Coche actif */}
                    {isActive && !isLocked && (
                      <span className="bg-picker-check">
                        <Check size={11} strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <span className="bg-picker-label">{bg.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        /* ── Dropdown container ── */
        .theme-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 214px;
          border-radius: 16px;
          padding: 6px;
          background: #ffffff;
          border: 1px solid rgba(15,23,42,0.07);
          box-shadow:
            0 16px 40px rgba(15,23,42,0.14),
            0 0 0 1px rgba(148,163,184,0.07);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          animation: tdFadeIn 0.17s ease-out both;
          z-index: 1200;
        }
        @keyframes tdFadeIn {
          from { opacity:0; transform:translateY(-6px) scale(0.97); }
          to   { opacity:1; transform:translateY(0)    scale(1);    }
        }

        /* ── Items ── */
        .theme-dropdown__item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 11px;
          border: none;
          background: transparent;
          cursor: pointer;
          text-align: left;
          transition: background 0.15s ease;
          color: #111827;
        }
        .theme-dropdown__item:hover { background: #f0f4f8; }

        .theme-dropdown__icon {
          display: flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; border-radius: 8px;
          background: #f3f4f6; color: #374151; flex-shrink: 0;
        }

        .theme-dropdown__label { flex: 1; font-size: 13.5px; font-weight: 600; line-height: 1; }

        .theme-dropdown__toggle {
          flex-shrink: 0; width: 36px; height: 20px; border-radius: 999px;
          background: #e5e7eb; position: relative; transition: background 0.2s ease;
        }
        .theme-dropdown__item:has([data-active="true"]) .theme-dropdown__toggle { background: #76A5A7; }
        .theme-dropdown__pill {
          position: absolute; top: 3px; left: 3px;
          width: 14px; height: 14px; border-radius: 50%;
          background: #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,0.18);
          transition: transform 0.2s ease;
        }
        .theme-dropdown__pill[data-active="true"] { transform: translateX(16px); }

        .theme-dropdown__arrow {
          font-size: 16px; color: #9ca3af; flex-shrink: 0;
          transition: transform 0.15s ease, color 0.15s ease;
        }
        .theme-dropdown__item:hover .theme-dropdown__arrow {
          transform: translateX(3px); color: #61837A;
        }

        .theme-dropdown__divider {
          height: 1px; margin: 4px 6px; background: rgba(15,23,42,0.06);
        }

        /* ── Sous-menu picker ── */
        .theme-dropdown--picker {
          min-width: 240px;
          padding: 6px 6px 8px;
        }

        .theme-dropdown__back {
          width: 100%; display: flex; align-items: center; gap: 7px;
          padding: 8px 12px; border-radius: 11px; border: none;
          background: transparent; cursor: pointer;
          font-size: 13px; font-weight: 700; color: #374151;
          transition: background 0.15s ease;
        }
        .theme-dropdown__back:hover { background: #f0f4f8; }
        .theme-dropdown__back svg { color: #9ca3af; flex-shrink: 0; }

        /* Grille 2 colonnes */
        .bg-picker-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 6px;
          padding: 4px 2px 2px;
        }

        /* Chaque item */
        .bg-picker-item {
          display: flex; flex-direction: column; align-items: center; gap: 5px;
          padding: 4px;
          border-radius: 10px; border: 2px solid transparent;
          background: transparent; cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .bg-picker-item:hover {
          background: rgba(118,165,167,0.08);
          border-color: rgba(118,165,167,0.3);
        }
        .bg-picker-item--active {
          border-color: #76A5A7;
          background: rgba(118,165,167,0.12);
        }

        /* ── État verrouillé ── */
        .bg-picker-item--locked {
          cursor: not-allowed;
          opacity: 1; /* on garde l'opacité normale, c'est le thumb qui est grisé */
        }
        .bg-picker-item--locked:hover {
          background: transparent;
          border-color: transparent;
        }

        /* Vignette */
        .bg-picker-thumb {
          position: relative;
          width: 100%; aspect-ratio: 16/9;
          border-radius: 7px; overflow: hidden;
          background-size: cover; background-position: center;
          background-color: #e5e7eb;
          box-shadow: 0 2px 6px rgba(0,0,0,0.12);
          flex-shrink: 0;
        }

        /* Grayscale sur la vignette si item verrouillé */
        .bg-picker-item--locked .bg-picker-thumb {
          filter: grayscale(1) brightness(0.7);
        }

        /* Overlay cadenas */
        .bg-picker-lock-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          background: rgba(0,0,0,0.28);
          color: #ffffff;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.02em;
        }

        /* Coche active */
        .bg-picker-check {
          position: absolute; bottom: 4px; right: 4px;
          width: 18px; height: 18px; border-radius: 50%;
          background: #76A5A7; color: #ffffff;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }

        /* Label sous la vignette */
        .bg-picker-label {
          font-size: 11px; font-weight: 600;
          color: #374151; text-align: center;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          max-width: 100%; line-height: 1;
        }
        .bg-picker-item--active .bg-picker-label { color: #61837A; }

        /* Label grisé si verrouillé */
        .bg-picker-item--locked .bg-picker-label {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
}